"use client";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, useGLTF } from "@react-three/drei";
import * as THREE from "three";
import { useRef, useEffect, useState } from "react";
import { a, useSpring } from "@react-spring/three";
import { useBreakpoint } from "hooks/utils";

interface PhoneModelProps {
	videoSrc: string;
	delay?: number;
	positionTarget?: number; // x position when it stops
	tilt?: { x: number; y: number; z: number }; // custom tilt
	scale?: number; // scale of the phone model
}

function PhoneModel({
	videoSrc,
	delay = 0,
	positionTarget = -1,
	tilt = { x: -0.05, y: 0, z: 0.05 },
	scale = 0.15,
}: PhoneModelProps) {
	const { scene } = useGLTF("/models/iphone-12-pro-max.glb");
	const phoneRef = useRef<THREE.Group>(null);

	const videoRef = useRef<HTMLVideoElement | null>(null);
	const videoTexture = useRef<THREE.VideoTexture | null>(null);
	const [videoAspect, setVideoAspect] = useState(9 / 16);

	// Spring animation
	const spring = useSpring({
		from: { x: -10, rotationX: 0, rotationY: 0, rotationZ: 0 },
		to: {
			x: positionTarget,
			rotationX: tilt.x,
			rotationY: tilt.y,
			rotationZ: tilt.z,
		},
		config: { mass: 2, tension: 50, friction: 25 },
		delay,
	});

	// Setup video
	if (!videoTexture.current) {
		const video = document.createElement("video");
		video.src = videoSrc;
		video.crossOrigin = "Anonymous";
		video.loop = true;
		video.muted = true;
		video.playsInline = true;
		video.autoplay = true;
		videoRef.current = video;

		video.addEventListener("canplay", () => {
			video.play().catch(() => {});
		});
		video.addEventListener("loadedmetadata", () => {
			setVideoAspect(video.videoWidth / video.videoHeight);
		});

		const texture = new THREE.VideoTexture(video);
		// texture.colorspace = THREE.SRGBColorSpace;
		texture.minFilter = THREE.LinearFilter;
		texture.magFilter = THREE.LinearFilter;
		texture.generateMipmaps = false;
		videoTexture.current = texture;
	}

	// Add rounded plane with video
	useEffect(() => {
		if (!phoneRef.current || !videoTexture.current) return;

		const screenHeight = 15.5;
		const screenWidth = screenHeight * (9 / 19.5);
		const radius = 1;

		const shape = new THREE.Shape();
		shape.moveTo(-screenWidth / 2 + radius, -screenHeight / 2);
		shape.lineTo(screenWidth / 2 - radius, -screenHeight / 2);
		shape.quadraticCurveTo(
			screenWidth / 2,
			-screenHeight / 2,
			screenWidth / 2,
			-screenHeight / 2 + radius
		);
		shape.lineTo(screenWidth / 2, screenHeight / 2 - radius);
		shape.quadraticCurveTo(
			screenWidth / 2,
			screenHeight / 2,
			screenWidth / 2 - radius,
			screenHeight / 2
		);
		shape.lineTo(-screenWidth / 2 + radius, screenHeight / 2);
		shape.quadraticCurveTo(
			-screenWidth / 2,
			screenHeight / 2,
			-screenWidth / 2,
			screenHeight / 2 - radius
		);
		shape.lineTo(-screenWidth / 2, -screenHeight / 2 + radius);
		shape.quadraticCurveTo(
			-screenWidth / 2,
			-screenHeight / 2,
			-screenWidth / 2 + radius,
			-screenHeight / 2
		);

		const geometry = new THREE.ShapeGeometry(shape);

		// Remap UVs
		geometry.computeBoundingBox();
		const { min, max } = geometry.boundingBox!;
		const range = new THREE.Vector2(max.x - min.x, max.y - min.y);
		const uvs = geometry.attributes.uv;
		for (let i = 0; i < uvs.count; i++) {
			uvs.setX(i, (uvs.getX(i) - min.x) / range.x);
			uvs.setY(i, (uvs.getY(i) - min.y) / range.y);
		}
		uvs.needsUpdate = true;

		const material = new THREE.MeshBasicMaterial({
			map: videoTexture.current,
			toneMapped: false,
		});

		const plane = new THREE.Mesh(geometry, material);
		plane.position.set(0, 0, 0.3);
		phoneRef.current.add(plane);
	}, [phoneRef, videoTexture, videoAspect]);

	return (
		<a.group
			ref={phoneRef}
			scale={scale}
			position-x={spring.x}
			rotation-x={spring.rotationX}
			rotation-y={spring.rotationY}
			rotation-z={spring.rotationZ}
		>
			<primitive object={scene.clone(true)} />
		</a.group>
	);
}

export const PhoneHero = () => {
	const { breakpointSmallerThan } = useBreakpoint();
	const isMobile = breakpointSmallerThan("md");

	return (
		<Canvas
			camera={{ position: [0, 0, 4.5], fov: 35 }}
			style={{
				width: "100%",
				height: "100%",
				marginTop: -50,

				touchAction: "none", // prevent pan/zoom hijacking
				pointerEvents: "none",
			}}
		>
			<ambientLight intensity={1} />
			<directionalLight position={[5, 5, 5]} intensity={0.8} />

			{/* First phone */}
			<PhoneModel
				videoSrc="/videos/feed-comment-emoji.mp4"
				delay={300}
				positionTarget={isMobile ? 0 : -1}
				tilt={{ x: -0.05, y: 4 * Math.PI + 0.6, z: 0.05 }}
				scale={isMobile ? 0.125 : 0.15}
			/>

			{/* Second phone slides in slightly after first */}
			{!isMobile ? (
				<PhoneModel
					videoSrc="/videos/track-workout-and-see-records.mp4"
					delay={1200} // 500ms after first
					positionTarget={1}
					tilt={{ x: -0.08, y: 2 * Math.PI - 0.6, z: -0.05 }} // tilted back, opposite side
					scale={0.15}
				/>
			) : null}

			<OrbitControls
				enableZoom={false}
				enablePan={false}
				enableRotate={false}
			/>
		</Canvas>
	);
};
